import { useMemo } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { LayoutGrid, LogOut, User } from 'lucide-react'

import { useAuth } from '@/services/auth/use-auth'
import { queryUserInformation } from '@/services/user/query'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { getFallbackName } from '@/lib/utils/utils'

interface UserNavProps {
  user: {
    name: string
    email: string
    avatar?: string
  }
}

export function UserNav({ user }: UserNavProps) {
  const router = useRouter()
  const navigate = useNavigate()
  const logout = useAuth((s) => s.logout)

  const { data: userPersonalInfo } = useQuery(queryUserInformation())
  const fallbackName = useMemo(
    () => getFallbackName(user?.name || userPersonalInfo?.name || user.email),
    [user]
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={userPersonalInfo ? userPersonalInfo?.photo : '#'}
              alt="Avatar"
            />
            <AvatarFallback className="bg-transparent">
              {fallbackName}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* <DropdownMenuGroup>
              <DropdownMenuItem className="hover:cursor-pointer" asChild>
                <Link to="/" className="flex items-center">
                  <LayoutGrid className="mr-3 h-4 w-4 text-muted-foreground" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="hover:cursor-pointer" asChild>
                <Link to="/" className="flex items-center">
                  <User className="mr-3 h-4 w-4 text-muted-foreground" />
                  Account
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup> */}
        {/* <DropdownMenuSeparator /> */}
        <DropdownMenuItem
          className="hover:cursor-pointer"
          onClick={() => {
            logout().then(() => {
              navigate({ to: '/auth/login' })
              router.invalidate()
            })
          }}
        >
          <LogOut className="mr-3 h-4 w-4 text-muted-foreground" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
